import { useLayoutEffect, useRef } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import {
    useGridApiContext,
    type GridRenderEditCellParams
} from '../lib/dataGrid';
import { joinedToChips, chipsToJoined } from './formFields';
import { useRecentValueEntry } from './recentValueEntry';
import { renderCheckableOption } from './checkableOption';
import { useChipClipboard } from './chipClipboard';

interface GridMultiAutocompleteEditCellProps extends GridRenderEditCellParams {
    /** Suggestions offered while typing; free-text entries are still allowed. */
    options: string[];
    /** The last values of this field, newest first — the F2 entry. */
    recent?: readonly string[];
}

/**
 * Grid edit cell for a field holding SEVERAL values — the Zeichner of a sheet,
 * the Planarten on it. The same chips the record form shows, in the cell.
 *
 * The cell value stays the ', '-joined string the column already displays, so
 * the grid, the Sammelbearbeitung and the form all pass one shape around; the
 * field spec's `parse` turns it into the stored array at the one boundary where
 * it matters (see gridInlineEdit). Splitting is {@link joinedToChips}, the same
 * rule the form's joined multi-select uses.
 */
export default function GridMultiAutocompleteEditCell(
    props: GridMultiAutocompleteEditCellProps
) {
    const { id, field, value, hasFocus, options, recent } = props;
    const { error } = props as GridMultiAutocompleteEditCellProps & {
        error?: string | null;
    };
    const apiRef = useGridApiContext();
    const inputRef = useRef<HTMLInputElement>(null);

    useLayoutEffect(() => {
        if (hasFocus) {
            inputRef.current?.focus();
        }
    }, [hasFocus]);

    // The vocabulary decides where the entries end — an option may itself
    // contain a comma (see joinedToChips).
    const chips = joinedToChips(value, options);

    // A whole SET of chips at once — "same Zeichner as the last sheet" is one
    // key rather than three pickings (see ./recentValueEntry).
    // No button in a cell — see ValidatedGridEditCell.
    const { onKeyDown, menu } = useRecentValueEntry({
        recent,
        value: String(value ?? ''),
        onPick: (picked) =>
            apiRef.current.setEditCellValue({ id, field, value: picked })
    });

    const clipboard = useChipClipboard({
        value: chips,
        getLabel: (chip: string) => chip,
        options,
        fromText: (text) => text,
        onChange: (next) =>
            apiRef.current.setEditCellValue({
                id,
                field,
                value: chipsToJoined(next)
            })
    });

    return (
        <Tooltip
            open={!!error && hasFocus}
            title={error ?? ''}
            arrow
            placement="bottom"
        >
            {/* One child for the Tooltip; the Menu portals itself out. */}
            <span style={{ display: 'flex', width: '100%' }}>
                <Autocomplete
                    multiple
                    disableCloseOnSelect
                    renderOption={renderCheckableOption({ ticks: true })}
                    freeSolo
                    fullWidth
                    disableClearable
                    size="small"
                    options={options}
                    value={chips}
                    onKeyDown={onKeyDown}
                    onChange={(_event, next) =>
                        apiRef.current.setEditCellValue({
                            id,
                            field,
                            value: chipsToJoined(next as string[])
                        })
                    }
                    {...clipboard}
                    sx={{ height: '100%' }}
                    renderInput={(params) => (
                        <TextField
                            {...params}
                            inputRef={inputRef}
                            variant="standard"
                            error={!!error}
                            sx={{
                                height: '100%',
                                '& .MuiInputBase-root': {
                                    height: '100%',
                                    fontSize: 'inherit',
                                    paddingLeft: '9px',
                                    /**
                                     * Chips + a text box in one row height: what does
                                     * not fit scrolls rather than pushing the row
                                     * open, which would shift every row below it.
                                     */
                                    flexWrap: 'nowrap',
                                    overflowX: 'auto'
                                },
                                '& .MuiInputBase-root::before, & .MuiInputBase-root::after':
                                    {
                                        borderBottom: 'none !important'
                                    }
                            }}
                        />
                    )}
                />
                {menu}
            </span>
        </Tooltip>
    );
}
